import "../style.scss";

import { Breadcrumbs } from "@amzn/stencil-react-components/breadcrumbs";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row, Spacer } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { Select } from "@amzn/stencil-react-components/select";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import TextArea from "@amzn/uxm-stencil-react-components/lib/src/stencil/text-area";
import { UserAvatar } from "common/components/UserAvatar";
import { trackUxmCustomEvent } from "common/helpers/analytics-helper";
import * as constants from "common/types/constants";
import { getString, replacePlaceholders } from "common/uistringlabels/uiStringUtils";
import { UserPermissions } from "lib/services";
import { FAQ, HardwareSupportFaq } from "lib/services/hardware-support-faq-service";
import { debounce } from "lodash";
import React, { FC, useEffect, useState } from "react";

import { useHardwareSupport } from "../useHardwareSupport";

/**
 * Represents the props for the HardwareOrderSupport component.
 *
 * @interface
 * @property {() => void} [onSendMessageClick] - Callback function triggered when the send message button is clicked, will update the view to success message.
 * @property {() => void} [onBackClick] - Callback function triggered when the back button is clicked.
 */
interface HardwareOrderSupportProps {
  onSendMessageClick: () => void;
  onBackClick: () => void;
}

/**
 * Represents a selectable option used in dropdowns.
 *
 * @interface
 * @property {string} value - The unique identifier of the option.
 * @property {string} label - The display text of the option.
 */
interface SelectOption {
  value: string;
  label: string;
}

/**
 * HardwareOrderSupport component provides an interface for users to get support
 * for their hardware orders. Users can select an order, choose from FAQ issues,
 * or submit custom support requests.
 *
 * @param {HardwareOrderSupportProps} props - Component props
 * @returns JSX element for hardware order support interface
 */
export const HardwareOrderSupport: FC<HardwareOrderSupportProps> = ({
  onSendMessageClick,
  onBackClick,
}: HardwareOrderSupportProps) => {
  // State management
  const [selectedQuestion, setSelectedQuestion] = useState<string>(constants.EMPTY_STRING);
  const [selectedOrder, setSelectedOrder] = useState<string | undefined>(undefined);
  const [isCustomIssue, setIsCustomIssue] = useState<boolean>(false);
  const [disableIssueSelect, setDisableIssueSelect] = useState<boolean>(true);
  const [faqData, setFaqData] = useState<FAQ[]>(constants.EMPTY_ARRAY);
  const [message, setMessage] = useState<string>(constants.EMPTY_STRING);

  // Hooks and services
  const { orderList, submitComment, isLoading, hasError } = useHardwareSupport(constants.ITOConsumers.AMZNHAM);
  const hardwareSupportFaqService = new HardwareSupportFaq();

  const getFaqData = async () => {
    const faqData = await hardwareSupportFaqService.getHardwareSupportFaq();
    setFaqData(faqData);
  };

  useEffect(() => {
    if (!selectedOrder) return;
    getFaqData();
  }, [selectedOrder]);

  // Derived data
  const selectedFaq = faqData.find((faq) => faq.id === selectedQuestion);

  const issueOptions: SelectOption[] = faqData.map((faq) => ({
    value: faq.id,
    label: faq.question,
  }));

  const orderOptions: SelectOption[] = orderList.map((order) => ({
    value: order.orderTaxId,
    label: order.orderDisplay,
  }));

  const debouncedSendComment = debounce(sendComment, constants.HALF_SECOND_IN_MS);

  function handleSendComment() {
    trackUxmCustomEvent("send-comment-button", { selectedOrder, message });
    debouncedSendComment();
  }

  async function sendComment() {
    const commentSubmitted = await submitComment(selectedOrder!, message);

    if (commentSubmitted && !hasError) {
      onSendMessageClick();
      setMessage(constants.EMPTY_STRING);
    }
  }

  // Render helpers
  const renderOrdersOption = (option: SelectOption) => {
    const order = orderList.find((o) => o.orderTaxId === option.value);
    return (
      <Row gridGap={constants.SPACING_S200} alignItems={constants.ALIGN_ITEMS_CENTER}>
        <Text fontWeight="bold">{option.label}</Text>
        <Text>
          ({getString("hardwareSupport.orderNumberDisplay")}
          {order?.orderRef})
        </Text>
      </Row>
    );
  };

  const renderQuestionsOption = (option: SelectOption) => (
    <Row gridGap={constants.SPACING_S200} alignItems={constants.ALIGN_ITEMS_CENTER}>
      {/* TODO: Replace the placeholder, left with this value for Scafolding */}
      {replacePlaceholders(option.label, { location: "SJO11" })}
    </Row>
  );

  // Event handlers
  const handleQuestionChange = (selectedValue: SelectOption) => {
    trackUxmCustomEvent("faq-select", { option: selectedValue.value });
    setSelectedQuestion(selectedValue.value);
    setIsCustomIssue(selectedValue.value === constants.NOT_LISTED);
  };

  const handleOrderChange = (selectedValue: SelectOption) => {
    trackUxmCustomEvent("order-select", { option: selectedValue.value });
    setSelectedOrder(selectedValue.value);
    setDisableIssueSelect(false);
  };

  const renderCustomIssueForm = () => (
    <Col>
      <Spacer height={constants.SPACING_S200} />
      {UserPermissions.current.username && (
        <Row margin={{ bottom: constants.SPACING_S200 }}>
          <UserAvatar login={UserPermissions.current.username} name={getString("orderMessages.fromSelf")} />
        </Row>
      )}
      <Row>
        <TextArea
          id="message-text-area"
          data-testid="message-text-area"
          touchpoint="message-text-area"
          width={constants.ONE_HUNDRED_PERCENT_STRING}
          placeholder={getString("hardwareOrderSupport.messagePlaceholder")}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </Row>
      <Row justifyContent={constants.JUSTIFY_FLEX_END} padding={constants.SPACING_S200}>
        <Button
          id="submit-message-button"
          variant={ButtonVariant.Primary}
          onClick={handleSendComment}
          disabled={!message.trim() || isLoading}
        >
          {getString("hardwareOrderSupport.submitButton")}
          {isLoading ? <Spinner /> : null}
        </Button>
      </Row>
    </Col>
  );

  const renderErrorBanner = () => {
    return (
      <Row>
        <MessageBanner
          data-testid={"hw-support-error-banner"}
          dismissButtonAltText="Dismiss"
          hasRoundedCorners={true}
          type={MessageBannerType.Error}
          isDismissible={true}
        >
          {`${getString("errors.general")} ${getString("errors.persistmessage")}`}
          <Link target={constants.BLANK_STRING} href={getString("externalLinks.shopErrorTicketTemplate")}>
            {getString("errors.ctitext")}
          </Link>
        </MessageBanner>
      </Row>
    );
  };

  return (
    <Col>
      {hasError && renderErrorBanner()}
      <Row>
        <Breadcrumbs
          onCrumbClick={(e) => {
            e.preventDefault();
            onBackClick();
          }}
          crumbs={[{ title: "Get hardware support", url: "/" }, { title: "Get order support" }]}
        />
      </Row>

      <Row>
        <Text>{getString("hardwareOrderSupport.supportText")}</Text>
      </Row>

      <Spacer height={constants.SPACING_S500} />

      <Row>
        <Col width={constants.ONE_HUNDRED_PERCENT_STRING} gridGap={constants.SPACING_S200}>
          <Text fontWeight={constants.MEDIUM}>{getString("hardwareOrderSupport.orderSelectorTitle")}</Text>
          <Select
            id="order-select"
            data-testid={"select-order"}
            onChange={handleOrderChange}
            placeholder={getString("hardwareOrderSupport.orderSelectPlaceholder")}
            options={orderOptions}
            renderOption={renderOrdersOption}
          />
        </Col>
      </Row>

      <Spacer height={constants.SPACING_S500} />

      <Row>
        <Col width={constants.ONE_HUNDRED_PERCENT_STRING} gridGap={constants.SPACING_S200}>
          <Text fontWeight={constants.MEDIUM}>{getString("hardwareOrderSupport.issueSelectorTitle")}</Text>
          <Select
            id="faq-select"
            data-testid={"select-issue"}
            onChange={handleQuestionChange}
            placeholder={getString("hardwareOrderSupport.issueSelectPlaceholder")}
            options={issueOptions}
            renderOption={renderQuestionsOption}
            disabled={disableIssueSelect}
          />

          {!isCustomIssue && selectedFaq?.answer && (
            <Text id="faq-response" data-testid={"issue-response"}>
              {/* TODO: Replace the placeholder, left with this value for Scafolding */}
              {replacePlaceholders(selectedFaq.answer, { date: "11-25-2025" })}
            </Text>
          )}

          {isCustomIssue && renderCustomIssueForm()}
        </Col>
      </Row>
    </Col>
  );
};
